import DOMPurify from 'dompurify';
import { marked } from 'marked';

// Configure marked for safe rendering
marked.setOptions({
  breaks: true,
  gfm: true,
});

/**
 * Convert markdown to HTML
 * Why this matters: Backend generates content in markdown format, needs conversion for display
 */
export const markdownToHTML = (markdown: string): string => {
  if (!markdown) return '';
  
  // Always convert markdown to HTML - marked handles mixed content well
  // This ensures [text](url) links are converted to <a> tags
  try {
    return marked.parse(markdown) as string;
  } catch (e) {
    console.error('Error parsing markdown:', e);
    return markdown;
  }
};

/**
 * Sanitize HTML content to prevent XSS attacks
 * Allows safe HTML tags while removing potentially dangerous content
 */
export const sanitizeHTML = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      // Text formatting
      'p', 'br', 'strong', 'em', 'u', 'mark', 'small', 'del', 'ins', 'sub', 'sup',
      // Headings
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      // Lists
      'ul', 'ol', 'li',
      // Links
      'a',
      // Images
      'img', 'figure', 'figcaption',
      // Tables
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
      // Semantic
      'blockquote', 'cite', 'code', 'pre',
      // Divisions
      'div', 'span', 'section', 'article', 'aside', 'header', 'footer', 'nav',
      // Style blocks - needed so injected content sections (e.g. the Dr. Mani
      // Brown Thumb Field Guide) render styled in the preview. Safe because
      // content comes from our own backend and DOMPurify still strips scripts.
      'style',
    ],
    ALLOWED_ATTR: [
      'href', 'title', 'target', 'rel',
      'class', 'id', 'style',
      'src', 'alt', 'width', 'height', 'loading',
      'colspan', 'rowspan',
      'aria-label', 'aria-describedby',
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    // Always open links in new tab for security
    ADD_ATTR: ['target'],
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    FORCE_BODY: false,
    SANITIZE_DOM: true,
    WHOLE_DOCUMENT: false,
  });
};

/**
 * Sanitize HTML and ensure external links open in new tab
 * Also converts markdown to HTML if needed
 */
export const sanitizeHTMLForDisplay = (content: string): string => {
  if (!content) return '';
  
  // First convert markdown to HTML (handles markdown links like [text](url))
  const html = markdownToHTML(content);
  
  // Then sanitize the HTML
  const sanitized = sanitizeHTML(html);

  // Ensure external links have target="_blank" and rel="noopener noreferrer"
  const doc = new DOMParser().parseFromString(sanitized, 'text/html');
  const links = doc.querySelectorAll('a[href]');

  links.forEach((link) => {
    const href = link.getAttribute('href');
    if (href && !href.startsWith('#') && !href.startsWith('/')) {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    }
  });

  return doc.body.innerHTML;
};
